import SEOHead from '@/components/base/SEOHead';
import PageJsonLd from '@/components/base/PageJsonLd';
import { CONTACT_API } from '@/config';
import Layout from '@/components/feature/Layout';
import Button from '@/components/base/Button';

export default function Contact() {
  return (
    <Layout>
      <SEOHead
        title="お問い合わせ｜デモ・導入相談のご予約｜”おもいむすび”"
        description="”おもいむすび”に関するお問い合わせ、デモ・導入相談のご予約はこちらから。AIプロジェクト管理ツールの導入について、お気軽にご相談ください。"
        keywords="プロジェクト管理ツール 問い合わせ,AI プロジェクト管理 相談,デモ 予約,導入相談"
        canonical="https://omoimusubi.asia/contact/"
      />
      <PageJsonLd
        canonical="https://omoimusubi.asia/contact/"
        name="お問い合わせ"
        description="”おもいむすび”に関するお問い合わせ、デモ・導入相談のご予約はこちらから。AIプロジェクト管理ツールの導入について、お気軽にご相談ください。"
      />

      <section className="py-16 md:py-20 bg-background-50">
        <div className="max-w-[800px] mx-auto px-4 md:px-6">
          <h1 className="text-2xl md:text-4xl font-bold text-foreground-950 text-center mb-4">
            お問い合わせ
          </h1>
          <p className="text-base text-foreground-600 text-center mb-12">
            ”おもいむすび”に関するご質問、デモ・導入相談のご予約はこちらからお気軽にお問い合わせください。
          </p>

          <div className="not-prose mb-10 rounded-xl overflow-hidden border border-background-200/70">
            <img
              src="https://readdy.ai/api/search-image?query=A%20bright%20modern%20Japanese%20office%20reception%20area%20scene%20a%20friendly%20Japanese%20female%20staff%20member%20in%20her%2030s%20wearing%20business%20casual%20attire%20standing%20at%20a%20clean%20white%20desk%20with%20a%20warm%20welcoming%20smile%20she%20is%20looking%20at%20a%20laptop%20ready%20to%20assist%20customers%20natural%20daylight%20through%20large%20windows%20indoor%20green%20plants%20minimalist%20Japanese%20contemporary%20office%20interior%20refreshing%20professional%20customer%20service%20atmosphere&width=1200&height=600&seq=contact-hero-01&orientation=landscape"
              alt="お問い合わせのイメージ - お客様を迎えるビジネスパーソン"
              className="w-full h-auto"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-12 not-prose">
            <div className="bg-background-100 rounded-lg p-6 border border-background-200/70 text-center">
              <div className="w-12 h-12 mx-auto bg-primary-100 rounded-full flex items-center justify-center mb-3">
                <i className="ri-chat-3-line text-primary-600 text-xl"></i>
              </div>
              <h3 className="text-base font-bold text-foreground-900 mb-2">フォームでのお問い合わせ</h3>
              <p className="text-sm text-foreground-500">下記フォームからどうぞ</p>
              <p className="text-xs text-foreground-500 mt-1">通常1営業日以内にご返信</p>
            </div>
            <div className="bg-background-100 rounded-lg p-6 border border-background-200/70 text-center">
              <div className="w-12 h-12 mx-auto bg-accent-100 rounded-full flex items-center justify-center mb-3">
                <i className="ri-mail-line text-accent-600 text-xl"></i>
              </div>
              <h3 className="text-base font-bold text-foreground-900 mb-2">メールでのお問い合わせ</h3>
              <p className="text-sm text-foreground-500">24時間受付</p>
              <p className="text-xs text-foreground-500 mt-1">通常1営業日以内にご返信</p>
            </div>
          </div>

          <form
            data-readdy-form
            action="#"
            method="POST"
            className="bg-background-100 rounded-lg border border-background-200/70 p-8"
            onSubmit={async (e) => {
              e.preventDefault();
              const form = e.currentTarget;
              const honeypot = (form.querySelector('[name="company_alt"]') as HTMLInputElement)?.value?.trim();
              if (honeypot) {
                const msg = document.getElementById('contact-msg');
                if (msg) { msg.textContent = 'お問い合わせを受け付けました。担当者よりご連絡いたします。'; msg.className = 'text-green-600 text-sm text-center mt-4'; }
                return;
              }
              const fd = new FormData(form);
              fd.delete('company_alt');
              try {
                const body = Object.fromEntries(fd.entries()); const res = await fetch(CONTACT_API, { method: 'POST', body: JSON.stringify({ ...body, kind: 'contact' }), headers: { 'Content-Type': 'application/json' } });
                const text = await res.text();
                let parsed;
                try { parsed = JSON.parse(text); } catch { parsed = null; }
                const msg = document.getElementById('contact-msg');
                if (res.ok && parsed?.ok) {
                  if (msg) { msg.textContent = 'お問い合わせを受け付けました。担当者よりご連絡いたします。'; msg.className = 'text-green-600 text-sm text-center mt-4'; }
                  form.reset();
                } else {
                  const serverMsg = parsed?.meta?.message || parsed?.message || '送信に失敗しました。';
                  if (msg) { msg.textContent = serverMsg; msg.className = 'text-red-600 text-sm text-center mt-4'; }
                }
              } catch {
                const msg = document.getElementById('contact-msg');
                if (msg) { msg.textContent = '通信エラーが発生しました。再度お試しください。'; msg.className = 'text-red-600 text-sm text-center mt-4'; }
              }
            }}
          >
            <h2 className="text-lg font-bold text-foreground-900 mb-6">お問い合わせフォーム</h2>
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="contact-company" className="block text-sm font-medium text-foreground-700 mb-1">会社名 <span className="text-red-500">*</span></label>
                  <input type="text" id="contact-company" name="company" required className="w-full px-4 py-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent" />
                </div>
                <div>
                  <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1">お名前 <span className="text-red-500">*</span></label>
                  <input type="text" id="contact-name" name="name" required className="w-full px-4 py-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent" />
                </div>
              </div>
              <div>
                <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1">メールアドレス <span className="text-red-500">*</span></label>
                <input type="email" id="contact-email" name="email" required className="w-full px-4 py-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent" />
              </div>
              <div>
                <label htmlFor="contact-type" className="block text-sm font-medium text-foreground-700 mb-1">お問い合わせ種別 <span className="text-red-500">*</span></label>
                <select id="contact-type" name="type" required className="w-full px-4 py-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent">
                  <option value="">選択してください</option>
                  <option value="demo">デモ・導入相談</option>
                  <option value="pricing">料金に関する質問</option>
                  <option value="function">機能に関する質問</option>
                  <option value="support">サポート・技術的な質問</option>
                  <option value="other">その他</option>
                </select>
              </div>
              <div>
                <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1">お問い合わせ内容 <span className="text-red-500">*</span></label>
                <textarea id="contact-message" name="message" rows={5} maxLength={500} required className="w-full px-4 py-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent resize-none"></textarea>
              </div>
              <div className="antispam-field">
                <input type="text" name="company_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
              </div>
              <Button type="submit" size="lg" className="w-full justify-center">
                送信する
              </Button>
            </div>
            <p id="contact-msg" className="text-sm text-center mt-4"></p>
          </form>
        </div>
      </section>
    </Layout>
  );
}